import {
  all,
  call,
  fork,
  put,
  select,
  takeEvery,
  take
} from "redux-saga/effects";
import { actions, getters } from "./model";
import { registerError } from "redux/modules/errors/actions";
import { eventDetails as getPortalEventDetails } from "redux/modules/portal/selectors";
import { apiCall } from "App/Data/sagas";
import * as FIELD_IDS from "@sessionboard/sessionboard-value-types/src/constants/standard-module-field-ids";
import * as R from "ramda";
import { showSnackbar } from "redux/modules/snackbar/actions";

import { EDIT_PARTICIPANT_FORM_ID, TAB_IDS } from "./constants";
import { actions as FormActions } from "ui-kit/Form/model";
import { getEditedField } from "./selectors";
import { decorateValueToSave } from "Portal/NewPortal/utils";

const init = function*({ payload: { sessionId } }) {
  try {
    yield all([
      put(actions.setLoading(true)),
      put(actions.setSessionId(sessionId))
    ]);

    const eventDetails = yield select(getPortalEventDetails);

    const {
      payload: {
        chairpersons = [],
        speakers = [],
        sponsors = [],
        moderators = [],
        exhibitors = []
      }
    } = yield call(apiCall, {
      method: "get",
      url: `/portal/event/${eventDetails.uuid}/session-content/${sessionId}/session-participants`
    });

    yield all([
      put(actions.setChairpersons(chairpersons)),
      put(actions.setSpeakers(speakers)),
      put(actions.setSponsors(sponsors)),
      put(actions.setModerators(moderators)),
      put(actions.setExhibitors(exhibitors)),
      put(actions.setSelectedTab(TAB_IDS.DETAILS))
    ]);
  } catch (error) {
    yield all([
      put(
        registerError([
          {
            system: error,
            user: "An error occurred getting content settings"
          }
        ])
      )
    ]);
  } finally {
    yield put(actions.setLoading(false));
  }
};

const loadTasks = function*() {
  try {
    yield put(actions.setLoadingTasks(true));

    const eventDetails = yield select(getPortalEventDetails);
    const sessionId = yield select(getters.sessionId);
    const contactId = yield select(getters.contactId);
    const accountId = yield select(getters.accountId);

    const { payload } = yield call(apiCall, {
      method: "get",
      url: `/portal/event/${
        eventDetails.uuid
      }/session-content/${sessionId}/session-participants/${contactId ||
        accountId}/tasks`
    });

    yield put(actions.setTasks(payload));

    yield put(actions.setLoadingTasks(false));
  } catch (error) {
    yield put(actions.setLoadingTasks(false));
    yield put(
      registerError([
        {
          system: error,
          user: "An error occurred while loading tasks"
        }
      ])
    );
  }
};

const refreshParticipantData = function*() {
  yield put(actions.setLoadingParticipant(true));

  const eventDetails = yield select(getPortalEventDetails);
  const sessionId = yield select(getters.sessionId);
  const contactId = yield select(getters.contactId);
  const accountId = yield select(getters.accountId);

  const { payload } = yield call(apiCall, {
    method: "get",
    url: `/portal/event/${
      eventDetails.uuid
    }/session-content/${sessionId}/session-participants/${contactId ||
      accountId}`
  });

  yield put(
    actions.setSelectedRecordData({
      bio: payload.bio,
      values: payload.values,
      fields: payload.fields,
      visibleFieldIds: payload.visible_field_ids,
      lockedFieldIds: payload.locked_field_ids
    })
  );

  yield put(actions.setLoadingParticipant(false));
};

const selectParticipant = function*({ payload: { contactId, accountId } }) {
  try {
    yield all([
      put(actions.setContactId(contactId)),
      put(actions.setAccountId(accountId))
    ]);

    yield call(refreshParticipantData);
  } catch (error) {
    yield put(actions.setLoadingParticipant(false));
    yield put(
      registerError([
        {
          system: error,
          user: "An error occurred while loading details"
        }
      ])
    );
  }
};

const fieldUpdate = function*({ id, fieldId }) {
  const newValue = yield select(getEditedField, {
    instanceId: id,
    fieldId
  });

  const processedValue = R.propOr(newValue, fieldId, {
    [FIELD_IDS.LOGO_IMAGE_URL]: R.pathOr(null, [0, "url"], newValue),
    [FIELD_IDS.BACKGROUND_IMAGE_URL]: R.pathOr(null, [0, "url"], newValue)
  });

  yield put(
    actions.setUpdatedFieldValue({
      fieldId,
      value: processedValue
    })
  );
};

const watchUpdateFields = function*() {
  for (;;) {
    const {
      meta: { instanceId, fieldId }
    } = yield take(FormActions.setFieldValue.type);
    if (instanceId === EDIT_PARTICIPANT_FORM_ID) {
      yield call(fieldUpdate, {
        id: instanceId,
        fieldId
      });
    }
  }
};

const resetEditData = function*() {
  yield all([
    put(actions.setSaving(false)),
    put(actions.setIsEditingDetails(false)),
    put(actions.setTransitionToTabIdAfterSave(null)),
    put(actions.setUpdatedFieldValues({})),
    put(actions.setHasUnSavedChangesModal(false))
  ]);
};

const cancelSave = function*() {
  // eslint-disable-next-line no-use-before-define
  yield call(refreshParticipantData);

  yield call(resetEditData);
};

const save = function*(
  { payload: { viaUnsavedChangesModal } } = {
    payload: { viaUnsavedChangesModal: false }
  }
) {
  try {
    yield put(actions.setSaving(true));

    const eventDetails = yield select(getPortalEventDetails);
    const fields = yield select(getters.fields);
    const updatedFieldValues = yield select(getters.updatedFieldValues);
    const lockedFieldIds = yield select(getters.lockedFieldIds);
    const transitionToTabIdAfterSave = yield select(
      getters.transitionToTabIdAfterSave
    );
    const sessionId = yield select(getters.sessionId);
    const contactId = yield select(getters.contactId);
    const accountId = yield select(getters.accountId);

    // decorate values to send to backend
    const formattedValues = Object.keys(updatedFieldValues).reduce(
      (a, fieldId) => {
        const field = fields.find(f => f.id === fieldId);
        const isLockedField = lockedFieldIds.includes(fieldId);

        if (!isLockedField && field) {
          const value = updatedFieldValues[fieldId];
          a.push(decorateValueToSave({ fieldId, field, value }));
        }
        return a;
      },
      []
    );

    // save data
    yield call(apiCall, {
      method: "post",
      url: `/portal/event/${
        eventDetails.uuid
      }/session-content/${sessionId}/session-participants/${contactId ||
        accountId}`,
      data: {
        values: formattedValues
      }
    });

    // show save confirmation
    yield put(showSnackbar({ message: "Details Saved" }));

    // eslint-disable-next-line no-use-before-define
    yield call(refreshParticipantData);

    // show new tab that the user originally was intending to go to
    if (viaUnsavedChangesModal && transitionToTabIdAfterSave) {
      yield put(actions.setSelectedTab(transitionToTabIdAfterSave));
    }

    yield call(resetEditData);

    return true;
  } catch (error) {
    yield put(actions.setSaving(false));
    yield put(
      registerError([
        {
          system: error,
          user: "An error occurred while saving details"
        }
      ])
    );
    return false;
  }
};

const onCancelUnsavedChanges = function*() {
  yield put(actions.setHasUnSavedChangesModal(false));
  yield call(resetEditData);
};

const onConfirmUnsavedChanges = function*() {
  yield call(resetEditData);
};

const watchSelectParticipant = function*() {
  yield takeEvery(actions.selectParticipant.type, selectParticipant);
};
const watchInit = function*() {
  yield takeEvery(actions.init.type, init);
};
const watchLoadTasks = function*() {
  yield takeEvery(actions.loadTasks.type, loadTasks);
};
const watchSave = function*() {
  yield takeEvery(actions.save.type, save);
};
const watchCancel = function*() {
  yield takeEvery(actions.onCancel.type, cancelSave);
};
const watchOnCancelUnsavedChanges = function*() {
  yield takeEvery(actions.onCancelUnsavedChanges.type, onCancelUnsavedChanges);
};
const watchOnConfirmUnsavedChanges = function*() {
  yield takeEvery(
    actions.onConfirmUnsavedChanges.type,
    onConfirmUnsavedChanges
  );
};

const rootSaga = function*() {
  yield all([
    fork(watchInit),
    fork(watchLoadTasks),
    fork(watchSelectParticipant),
    fork(watchSave),
    fork(watchCancel),
    fork(watchUpdateFields),
    fork(watchOnCancelUnsavedChanges),
    fork(watchOnConfirmUnsavedChanges)
  ]);
};

export default rootSaga;
